/**
 * Blog Search Component
 * Provides live search functionality for blog articles without page reload
 */
class BlogSearch extends HTMLElement {
  constructor() {
    super();
    
    this.searchInput = this.querySelector('input[type="text"]');
    this.searchButton = this.querySelector('button[type="submit"]');
    this.blogContainer = document.querySelector('.blog-articles');
    this.featuredArticle = document.querySelector('.featured-article');
    this.originalArticles = [];
    this.isSearching = false;
    
    // Store original articles for filtering
    this.storeOriginalArticles();
    
    this.init();
  }

  init() {
    if (!this.searchInput || !this.blogContainer) return;

    // Prevent form submission
    const form = this.searchInput.closest('form');
    if (form) {
      form.addEventListener('submit', this.handleFormSubmit.bind(this));
    }

    // Add event listeners
    this.searchInput.addEventListener('input', this.handleSearchInput.bind(this));
    this.searchInput.addEventListener('keydown', this.handleKeydown.bind(this));
    
    if (this.searchButton) {
      this.searchButton.addEventListener('click', this.handleSearchClick.bind(this));
    }

    // Add clear button functionality
    this.addClearButton();
  }

  storeOriginalArticles() {
    // Store the original HTML of articles for filtering
    const articles = this.blogContainer.querySelectorAll('.blog-articles__article');
    this.originalArticles = Array.from(articles).map(article => ({
      element: article.cloneNode(true),
      title: this.getArticleTitle(article),
      excerpt: this.getArticleExcerpt(article),
      tags: this.getArticleTags(article)
    }));
  }

  getArticleTitle(article) {
    const titleEl = article.querySelector('.card-article__title, .article-card__title, h3, h2');
    return titleEl ? titleEl.textContent.trim().toLowerCase() : '';
  }

  getArticleExcerpt(article) {
    const excerptEl = article.querySelector('.card-article__excerpt, .article-card__excerpt, .card__text');
    return excerptEl ? excerptEl.textContent.trim().toLowerCase() : '';
  }

  getArticleTags(article) {
    const tagsEl = article.querySelectorAll('.card-article__tag, .article-card__tag');
    return Array.from(tagsEl).map(tag => tag.textContent.trim().toLowerCase()).join(' ');
  }

  handleFormSubmit(e) {
    e.preventDefault();
    this.performSearch();
  }

  handleSearchInput(e) {
    // Only handle UI updates, don't perform search
    const query = this.searchInput.value.trim();
    
    // If search is completely cleared, restore original view
    if (query === '') {
      this.showAllArticles();
      this.showFeaturedArticle();
    }
  }

  handleKeydown(e) {
    if (e.key === 'Enter') {
      e.preventDefault();
      this.performSearch();
    }
    if (e.key === 'Escape') {
      this.clearSearch();
    }
  }

  handleSearchClick(e) {
    e.preventDefault();
    this.performSearch();
  }

  performSearch() {
    const query = this.searchInput.value.trim().toLowerCase();
    
    if (query === '') {
      this.showAllArticles();
      this.showFeaturedArticle();
      this.removeSearchHint();
      return;
    }

    // Don't search until user has entered at least 3 characters
    if (query.length < 3) {
      this.showSearchHint();
      return;
    }

    this.removeSearchHint();
    this.showSearchIndicator();
    this.hideFeaturedArticle();
    
    // Filter articles based on query
    const filteredArticles = this.originalArticles.filter(article => {
      return article.title.includes(query) || 
             article.excerpt.includes(query) || 
             article.tags.includes(query);
    });

    this.displayFilteredArticles(filteredArticles, query);
    this.hideSearchIndicator();
  }

  showSearchIndicator() {
    this.isSearching = true;
    this.searchButton.classList.add('loading');
    this.searchInput.setAttribute('aria-busy', 'true');
  }

  hideSearchIndicator() {
    this.isSearching = false;
    this.searchButton.classList.remove('loading');
    this.searchInput.removeAttribute('aria-busy');
  }

  displayFilteredArticles(articles, query) {
    // Clear current articles
    this.blogContainer.innerHTML = '';

    if (articles.length === 0) {
      this.showNoResults(query);
      return;
    }

    // Add filtered articles
    articles.forEach(article => {
      const articleClone = article.element.cloneNode(true);
      this.highlightSearchTerms(articleClone, query);
      this.blogContainer.appendChild(articleClone);
    });

    // Show search results count
    this.showSearchResultsCount(articles.length, query);
  }

  highlightSearchTerms(articleElement, query) {
    const textElements = articleElement.querySelectorAll('.card-article__title, .card-article__excerpt, .card__text');
    
    textElements.forEach(element => {
      const text = element.textContent;
      const regex = new RegExp(`(${this.escapeRegExp(query)})`, 'gi');
      
      if (regex.test(text)) {
        element.innerHTML = text.replace(regex, '<mark>$1</mark>');
      }
    });
  }

  escapeRegExp(string) {
    return string.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
  }

  showNoResults(query) {
    const noResultsHTML = `
      <div class="blog-search__no-results">
        <div class="no-results-content">
          <h3>No articles found</h3>
          <p>Sorry, we couldn't find any articles matching "<strong>${this.escapeHtml(query)}</strong>".</p>
        </div>
      </div>
    `;
    this.blogContainer.innerHTML = noResultsHTML;
  }

  showSearchResultsCount(count, query) {
    // Remove existing results info
    const existingInfo = document.querySelector('.blog-search__results-info');
    if (existingInfo) {
      existingInfo.remove();
    }

    const resultsInfo = document.createElement('div');
    resultsInfo.className = 'blog-search__results-info';
    resultsInfo.innerHTML = `
      <p>Found ${count} article${count !== 1 ? 's' : ''} for "<strong>${this.escapeHtml(query)}</strong>"</p>
      <button class="blog-search__clear-btn" onclick="this.closest('blog-search').clearSearch()">
        Clear search
      </button>
    `;
    
    // Insert before blog container
    this.blogContainer.parentNode.insertBefore(resultsInfo, this.blogContainer);
  }

  showAllArticles() {
    // Remove search results info
    const existingInfo = document.querySelector('.blog-search__results-info');
    if (existingInfo) {
      existingInfo.remove();
    }

    // Clear and restore original articles
    this.blogContainer.innerHTML = '';
    this.originalArticles.forEach(article => {
      this.blogContainer.appendChild(article.element.cloneNode(true));
    });
  }

  hideFeaturedArticle() {
    if (this.featuredArticle) {
      this.featuredArticle.style.display = 'none';
    }
  }

  showFeaturedArticle() {
    if (this.featuredArticle) {
      this.featuredArticle.style.display = '';
    }
  }

  showSearchHint() {
    // Remove existing hint if any
    this.removeSearchHint();
    
    const hintElement = document.createElement('div');
    hintElement.className = 'search-hint';
    hintElement.innerHTML = '<em>Please enter 3 or more characters to search</em>';
    
    // Insert after the search container
    const searchContainer = this.querySelector('.search-container');
    if (searchContainer) {
      searchContainer.parentNode.insertBefore(hintElement, searchContainer.nextSibling);
    }
  }

  removeSearchHint() {
    const existingHint = document.querySelector('.search-hint');
    if (existingHint) {
      existingHint.remove();
    }
  }



  clearSearch() {
    this.searchInput.value = '';
    this.showAllArticles();
    this.showFeaturedArticle();
    this.removeSearchHint();
    this.searchInput.focus();
    
    // Hide clear button
    const clearBtn = this.querySelector('.search-clear-btn');
    if (clearBtn) {
      clearBtn.style.display = 'none';
    }
  }

  addClearButton() {
    // Add a clear button to the search input
    const clearBtn = document.createElement('button');
    clearBtn.type = 'button';
    clearBtn.className = 'search-clear-btn';
    clearBtn.innerHTML = '×';
    clearBtn.style.display = 'none';
    clearBtn.setAttribute('aria-label', 'Clear search');
    
    clearBtn.addEventListener('click', this.clearSearch.bind(this));
    
    // Style the clear button
    clearBtn.style.cssText = `
      position: absolute;
      right: 50px;
      top: 50%;
      transform: translateY(-50%);
      background: none;
      border: none;
      font-size: 20px;
      cursor: pointer;
      padding: 0;
      width: 20px;
      height: 20px;
      display: flex;
      align-items: center;
      justify-content: center;
      color: #666;
    `;

    // Position the search container relatively
    const searchContainer = this.searchInput.closest('.search-container');
    if (searchContainer) {
      searchContainer.style.position = 'relative';
      searchContainer.appendChild(clearBtn);
      
      // Show/hide clear button based on input
      this.searchInput.addEventListener('input', () => {
        clearBtn.style.display = this.searchInput.value.length > 0 ? 'flex' : 'none';
      });
      
      // Initially hide the clear button
      clearBtn.style.display = 'none';
    }
  }

  escapeHtml(text) {
    const div = document.createElement('div');
    div.textContent = text;
    return div.innerHTML;
  }
}

customElements.define('blog-search', BlogSearch);

// Initialize when DOM is ready
document.addEventListener('DOMContentLoaded', function() {
  // Auto-initialize blog search if search container exists
  const searchContainer = document.querySelector('.search-container');
  if (searchContainer && !searchContainer.closest('blog-search')) {
    // Wrap the search container in blog-search element
    const blogSearch = document.createElement('blog-search');
    searchContainer.parentNode.insertBefore(blogSearch, searchContainer);
    blogSearch.appendChild(searchContainer);
  }
});
